<template>
  <section>
    <!-- Page content-->
    <div class="content-wrapper">
      <information v-if="isInformationModalVisible"></information>
      <div class="content-heading">
        Account summary
        <small data-localize="dashboard.WELCOME"></small>
      </div>
      <div v-if="!isEmptyAnnouncement() && hasBeenCreatedAccount" class="row">
        <div class="col-md-12">
          <div class="panel panel-default">
            <div  v-html="announcementHTMLWithSafeLinks"></div>
          </div>
        </div>
      </div>
      <div class="row">
        <spinner v-if="loading || hasBeenRedirected"></spinner>
        <div class="col-md-12">

          <div class="panel panel-default">
            <div v-if="isThereAnyUpcomingTransaction" class="alert alert-info mb-lg" role="alert">
                <h4><em class="icon icon-info pr-sm"></em>Please note that you have upcoming transactions</h4>
                <p>Click <router-link :to="{ name: 'UpcomingTransactionsForInvestor' }" title="Upcoming Transactions" replace>here</router-link> to see the transactions you need to provide direction on.</p>
            </div>
            <!-- Onboarding Welcome Message -->
            <div v-if="!hasBeenCreatedAccount" class="welcome text-center">
              <header class="welcome-header">
                  <h2 class="welcome-title">Thanks for registering!</h2>
              </header>
              <p class="welcome-paragraph">
                  <strong>Welcome to Calvert Impact Capital's investment platform.</strong><br>We invite you to <a target="_blank" rel="noopener" aria-describedby="new-window-0" href="https://www.calvertimpactcapital.org/about">learn about our work <em class="fa fa fa-external-link"></em></a>. And when you're ready, proceed to create a new account.
              </p>
              <div class="welcome-options">
                  <button class="card card-create" @click="goToAccountCreation" role="link">
                      <em class="icon-user-follow card-icon" aria-hidden="true"></em>
                      <h4 class="card-title" >Create a new account</h4>
                      <p>Let's create an account with just three easy steps.</p>
                  </button>
              </div>
            </div>
            <dismissable-alert
              title="You've purchased a Community Investment Note!"
              subtitle="Your investment will take 3 to 4 business days to be processed. You will receive a notification when it's completed."
              :closeAction="hidePurchaseCompletedAlert"
              :isShowing="showPurchaseCompleted"
            />
            <div v-if="hasBeenCreatedAccount">
              <div class="row-space-around">
                <div class="current-balance">
                  <p class="msg text-muted panel-title mt-lg mb-lg">Current account balance: <strong class="amountBalance">{{ currentAmountBalance }}</strong></p>
                </div>
                <button v-if="!disableInvestment && (userStatus && userStatus.status === 'verified') && !isInstitutionAccount && !isReadOnly" type="submit" class="btn btn-default btn-lg m-vertical btn-full-mobile" data-toggle="tooltip" data-placement="top" @click="goToNotePurchase">
                    Make an investment
                </button>
              </div>
              <h3 class="panel-title mt-lg mb-lg">Here are your last 45 days of transactions:</h3>

              <div v-if="transactions.length > 0" class="transactions-container">
                <div class="transactions-table-desktop" v-bind:class="{ 'table-fixed-header--md cols-5': transactions.length > 8, 'condensed': isThereAnyUpcomingTransaction }">
                  <table class="table table-striped bank-accounts">
                    <thead>
                      <tr>
                        <th class="text-center">Date</th>
                        <th class="text-center">Transaction type</th>
                        <th class="text-center">Note number</th>
                        <th class="text-right">Amount</th>
                        <th class="text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(t, index) in transactions" :key="index">
                        <td class="text-center" v-bind:class="{ 'td-blocked': shouldDisplayBlock }">{{formatDate(t.createdAt)}}</td>
                        <td class="text-center" v-bind:class="{ 'td-blocked': shouldDisplayBlock }">{{formatType(t.type, 'investor', t.fundingSourceId)}}</td>
                        <td class="text-center" v-bind:class="{ 'td-blocked': shouldDisplayBlock }">{{t.note.noteId}}</td>
                        <td class="text-right" v-bind:class="{ 'td-blocked': shouldDisplayBlock }">{{formatAmount(t.amount)}}</td>
                        <td class="text-center" v-bind:class="{ 'td-blocked': shouldDisplayBlock }">
                          <span class="label label-danger" v-if="isFailTransaction(t)">Failed</span>
                          <span class="label label-danger" v-if="isCancelTransaction(t)">Cancelled</span>
                          <span class="label label-warning" v-if="isPurchaseFailedTransaction(t)">Pending</span>
                          <span class="label label-danger" v-if="isPurchaseFailedTransactionWithSelectedErrorsCode(t)">Failed</span>
                          <span class="label label-warning" v-if="t.status == 'PENDING'">{{formatStatus(t.status)}}</span>
                          <span class="label label-success" v-if="t.status == 'PROCESSED'">{{formatStatus(t.status)}}</span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div class="transactions-cards-mobile">
                  <div v-for="(t, index) in transactions" :key="index" class="transaction-card">
                    <div class="transaction-card-header">
                      <div class="transaction-card-date">{{formatDate(t.createdAt)}}</div>
                      <span class="label label-danger" v-if="isFailTransaction(t)">Failed</span>
                      <span class="label label-danger" v-if="isCancelTransaction(t)">Cancelled</span>
                      <span class="label label-warning" v-if="isPurchaseFailedTransaction(t)">Pending</span>
                      <span class="label label-danger" v-if="isPurchaseFailedTransactionWithSelectedErrorsCode(t)">Failed</span>
                      <span class="label label-warning" v-if="t.status == 'PENDING'">{{formatStatus(t.status)}}</span>
                      <span class="label label-success" v-if="t.status == 'PROCESSED'">{{formatStatus(t.status)}}</span>
                    </div>
                    <div class="transaction-card-body">
                      <div class="transaction-card-row">
                        <span class="transaction-card-label">Transaction type:</span>
                        <span class="transaction-card-value">{{formatType(t.type, 'investor', t.fundingSourceId)}}</span>
                      </div>
                      <div class="transaction-card-row">
                        <span class="transaction-card-label">Note number:</span>
                        <span class="transaction-card-value">{{t.note.noteId}}</span>
                      </div>
                      <div class="transaction-card-row transaction-card-amount">
                        <span class="transaction-card-label">Amount:</span>
                        <span class="transaction-card-value">{{formatAmount(t.amount)}}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="panel panel-default" v-if="hasBeenCreatedAccount">
            <div class="updates-header">
              <h3 class="panel-title mt-lg mb-lg">Investor updates</h3>
              <h3 class="panel-title mt-lg mb-lg latest-updates">
                  <a
                    class="btn-right icon-right bottom-margin"
                    href="http://www.calvertimpactcapital.org/blog"
                    target="_blank"
                  >
                    See Latest Calvert Impact Capital Updates
                  </a>
              </h3>
            </div>
            <div class="updates-content">
              <button
                v-for="(d, index) in Object.values(summaryPageDocuments).filter(Boolean)"
                :key="`Card${index}`"
                class="card card-update card-update-content"
                role="link"
                @click="openDocument(d)"
              >
                <img class="card-icon" :src="getIconUrl(d.icon, d.iconName)" alt="icon" height="100"/>
                <p>{{d.tag}}</p>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- END row-->
    </div>
    <!-- END content wrapper-->
  </section>
</template>
<script>
  import { mapActions, mapState, mapMutations, mapGetters } from 'vuex';
  import moment from 'moment';
  import {
    formatDate,
    formatAmount,
    formatType,
    formatStatus,
    isStateRestricted,
    isFailTransaction,
    isCancelTransaction,
    isPurchaseFailedTransaction,
    logAction,
    openUrl,
    isPurchaseFailedTransactionWithSelectedErrorsCode,
    normalizeRichTextLinks,
  } from '@/util';
  import _ from 'lodash';
  import { detect } from 'detect-browser';
  import spinner from '@/components/widgets/Spinner';
  import DismissableAlert from '@/components/widgets/DismissableAlert';
  import OnboardingWizard from '@/components/OnboardingWizard';
  import information from '@/components/modals/Information';
  import TransactionTypes from '@/data/static/transactionTypes';
  import logActions from '@/data/static/logActions';
  import TransactionStatuses from '@/data/static/transactionStatuses';

  import { SHOW_INFORMATION_MODAL } from '@/store/mutation-types';

  const mix = {
    methods: {
      formatDate,
      formatAmount,
      formatType,
      formatStatus,
      isStateRestricted,
      isFailTransaction,
      isCancelTransaction,
      isPurchaseFailedTransaction,
      isPurchaseFailedTransactionWithSelectedErrorsCode,
    },
  };

  export default {
    name: 'accountSummary',
    data() {
      return {
        announcementHTML: '',
        loading: false,
        currentAmountBalance: 0,
        transactions: [],
        isThereAnyUpcomingTransaction: false,
        showPurchaseCompleted: false,
        shouldDisplayBlock: false,
        isIE: detect().name === 'ie',
        summaryPageDocuments: [],
      };
    },
    async beforeMount() {
      try {
        this.loading = true;
        if (!this.hasBeenCreatedAccount) {
          this.loading = false;
          return;
        }
        await this.checkUserStatus();
        if (!this.activeAccountId) {
          this.loading = false;
          return;
        }
        await this.getTransactionsSummary();
        await this.getAmountBalance();
        await this.fetchSummaryPageDocuments();
        await this.initializeAnnouncement();
        this.showPurchaseCompleted = this.$route.params.purchasedNote;
        this.loading = false;
      } catch (error) {
        this.loading = false;
      }
    },
    mixins: [mix],
    components: {
      spinner,
      information,
      'onboarding-wizard': OnboardingWizard,
      'dismissable-alert': DismissableAlert,
    },
    computed: {
      ...mapState('information', [
        'isInformationModalVisible',
      ]),
      ...mapState('home', [
        'userStatus',
      ]),
      ...mapState('app', [
        'activeAccountId',
        'activeAccount',
        'accountTypes',
        'user',
        'announcement',
      ]),
      ...mapGetters('app', [
        'hasBeenCreatedAccount',
        'hasBeenRedirected',
      ]),
      ...mapState('app', [
        'user',
      ]),
      announcementHTMLWithSafeLinks() {
        return normalizeRichTextLinks(this.announcementHTML);
      },
      isInstitutionAccount() {
        return this.activeAccount.accountType === this.accountTypes.INSTITUTION.name;
      },
      disableInvestment() {
        const { accounts, disableInvestment } = this.user;
        const account = accounts.find(acct => acct.id === this.activeAccount.id);
        return disableInvestment || (account ? account.disableInvestment : true);
      },
      isReadOnly() {
        return this.user && this.user.userType === 'READ_ONLY';
      },
    },
    methods: {
      ...mapMutations('information', {
        showInformationModal: SHOW_INFORMATION_MODAL,
      }),
      ...mapActions('home', [
        'getUserStatus',
      ]),
      ...mapActions('transaction', [
        'getAccountTransactionsSummary',
      ]),
      ...mapActions('account', [
        'getSummaryPageDocuments',
        'createDocumentView',
        'getCurrentAmountBalance',
        'createAccountDocumentPresignedUrl',
      ]),
      ...mapActions('user', [
        'getAnnouncement',
      ]),
      async initializeAnnouncement() {
        const announcement = await this.getAnnouncement();

        this.announcementHTML = announcement.html || '';
      },
      isEmptyAnnouncement() {
        const domParser = new DOMParser();
        const parsedAnnouncement = domParser.parseFromString(this.announcementHTML, 'text/html');
        const body = parsedAnnouncement.body;
        if (!body.firstChild) {
          return true;
        }
        return body.textContent.length === 0 ||
        body.firstChild.textContent.length === 0;
      },
      goToAccountCreation() {
        this.$router.push({ name: 'AccountCreation' });
      },
      async updateAccountSummary() {
        this.loading = true;
        try {
          if (!this.hasBeenCreatedAccount) {
            this.loading = false;
            return;
          }
          await this.checkUserStatus();
          if (!this.activeAccountId) {
            return;
          }
          await Promise.all([
            this.getTransactionsSummary(),
            this.getAmountBalance(),
            this.fetchSummaryPageDocuments(),
          ]);
        } finally {
          this.loading = false;
        }
      },
      goToNotePurchase() {
        const state = this.user.residentialAddress.state;
        if (this.isStateRestricted(state)) {
          const restrictedMessage = `At this time, we cannot accept investments from persons resident in ${state} state.
            Please contact Calvert Impact Capital at info@calvertimpact.org or call us at 800-248-0337 if you have questions.`;
          this.showInformationModal(restrictedMessage);
        } else if (
          this.userStatus.needs.includes('FUNDING_SOURCE') ||
          this.userStatus.status === 'document_migrated_user' ||
          this.userStatus.status === 'retry'
          ) {
          this.$router.push({ name: 'AddFundingSource' });
        } else {
          this.$router.push({ name: 'PurchaseNote' });
        }
      },
      async checkUserStatus() {
        try {
          const accountId = this.activeAccount.id;

          // Without a real account there is no status to fetch. Asking for
          // account 0 returns a 403, which the HttpService interceptor turns
          // into a redirect to Home.
          if (!accountId) {
            return;
          }

          const response = await this.getUserStatus({
            accountId,
          });
          const { status, needs } = response;

          if (status === 'kba') {
            this.$router.push({ name: 'KbaVerification' });
          } else if (status === 'document') {
            this.$router.push({ name: 'DocumentUpload' });
          } else if (status === 'retry') {
            this.$router.push({ name: 'AccountCreation' });
          } else if (status === 'suspended') {
            this.$router.push({ name: 'AccountSuspended' });
          } else if (!needs.includes('FUNDING_SOURCE') && needs.includes('NOTE')) {
            this.$router.push({ name: 'PurchaseNote', query: { first: 1 } });
          }
        } catch (error) {
          this.loading = false;
          console.error(error);
        }
      },
      async getAmountBalance() {
        const response = await this.getCurrentAmountBalance({ accountId: this.activeAccountId });
        this.currentAmountBalance = formatAmount(response.currentAmountBalance);
      },
      isTransactionAboutPrependingPayment({ type, status, actionTaken }) {
        return (type === TransactionTypes.INT_PAID
            || type === TransactionTypes.SELL_MATURE
            || type === TransactionTypes.INT_REINVEST
            || type === TransactionTypes.SELL_REINVEST
            || type === TransactionTypes.INT_DONATE
            || type === TransactionTypes.SELL_DONATE)
            && (status === TransactionStatuses.PREPENDING)
            && !actionTaken;
      },
      async getTransactionsSummary() {
        if (!this.activeAccountId) {
          return;
        }
        const transactions = await this.getAccountTransactionsSummary({
          accountId: this.activeAccountId,
        });
        this.transactions = transactions;
        this.shouldDisplayBlock = this.isIE && this.transactions.length > 8;
        this.isThereAnyUpcomingTransaction = _.some(transactions, transactionObject =>
          this.isTransactionAboutPrependingPayment(transactionObject));
      },
      async fetchSummaryPageDocuments() {
        this.summaryPageDocuments = await this.getSummaryPageDocuments({
          accountId: this.activeAccountId,
        });
      },
      getIconUrl(icon, iconName) {
        if (icon) {
          return icon.Url;
        }
        return require(`@/assets/img/${iconName}`); // eslint-disable-line
      },
      async openDocument(doc) {
        const res = await this.createAccountDocumentPresignedUrl({
          accountId: this.activeAccountId,
          payload: {
            key: doc.OriginalKey,
          },
        });
        openUrl(res.url);
        await this.logDocumentViewed({ ...doc, Url: res.url });
      },
      async logDocumentViewed(doc) {
        const { Key, LastModified, ETag, Url } = doc;
        await this.createDocumentView({
          fileName: Key,
          lastModified: moment(LastModified).toDate(),
          url: Url.split('?')[0],
          Etag: ETag,
          userId: this.user.id,
          accountId: this.activeAccountId,
        });
        const logMessage = `User U${this.user.id} viewed ${Key} document`;
        logAction({
          user: this.user.id,
          level: 'info',
          action: logActions.DOCUMENT_VIEW.name,
          message: logMessage,
        });
      },
      hidePurchaseCompletedAlert() {
        this.showPurchaseCompleted = false;
      },
    },
  };
</script>
<style lang="scss" scoped>
  .amountBalance {
    font-size: 17px;
    color: #000000;
  }
  .current-balance {
    margin: auto 0px;
  }
  .upcoming-transactions-message {
    margin-bottom: 20px;
  }
  .btn.disabled , .btn[disabled] {
    cursor: default;
    opacity: 0.5;
  }
  .btn.disabled:hover , .btn[disabled]:hover {
    cursor: default;
    opacity: 0.5;
    background-color: #00788d;
  }

  /* Welcome Styles */
  .welcome {
    background-color: white;
    color: #515253;
    border: none;
    padding-bottom: 40px;
    margin: 0 auto;
    p {
        font-size: 16px;
    }
  }
  .welcome-header {
    margin: 40px auto 0;
  }
  .welcome-title {
    color: #00788d;
  }
  .welcome-paragraph {
    max-width: 70%;
    margin: 20px auto;
    em {
      opacity: 0.65;
      font-size: 12px;
    }
    a {
      color: #00788d;
      &:hover em {
          opacity: 1;
      }
    }
  }
  .welcome-options {
    display: flex;
    justify-content: center;
    .card {
        max-width: 310px;
    }
  }
  .card {
    box-shadow: 0 1px 1px rgba(0,0,0,.05);
    position: relative;
    display: flex;
    align-items: center;
    flex-direction: column;
    min-width: 0;
    word-wrap: break-word;
    background-color: #fff;
    background-clip: border-box;
    border-radius: .25rem;
    cursor: pointer;
    padding: 40px;
    transition: all .4s ease-in-out;
    margin: 10px;
    color: #00788d;
    &:hover {
        text-decoration: none;
        color: #005261;
        background-color: #f6feff;
    }
    p{
        font-size: 14px;
    }
  }
  .card-icon {
    font-size: 5rem;
    margin-bottom: 20px;
  }
  .card-title {
    font-weight: normal;
  }
  .card-create {
    border: 1px solid #c3e6cb;
    &:hover, &:focus {
        text-decoration: none;
        color: #005261;
        background-color: rgba(#d4edda,.15);
        box-shadow: 0 0 4px rgba(#d4edda,.85);
    }
    .card-icon {
        color: #558c1d;
    }
  }
  .card-update {
    color: #000000;
    padding: 10px;
  }
  .card-update-content {
    border: 1px solid #BDC3C7;
    width: 220px;
    &:hover, &:focus {
      text-decoration: none;
      color: #005261;
      background-color: rgba(#B8BFC3,.15);
      box-shadow: 0 0 4px rgba(#B8BFC3,.85);
    }
    .card-icon {
      color: #BDC3C7;
    }
  }
  .table-fixed-header--md {
    height: 46vh;
    overflow-y: hidden;
    tbody{
      height: calc(46vh - 60px);
    }
  }
  .table-fixed-header--md.condensed {
    height: 38vh;
    tbody{
      height: calc(38vh - 60px);
    }
  }
  tbody tr td {
    padding-top: 8px;
    height: auto;
  }
  .td-blocked {
    display: block;
  }
  aside.aside + section {
    margin-bottom: 0!important;
  }
  .updates-header {
    display: flex;
    justify-content: space-between;
  }
  .updates-content {
    display: flex;
    justify-content: space-evenly;
  }
  .latest-updates {
    display: flex;
    place-content: space-around space-between;
    font-size: 17px;
  }
  .latest-updates a {
    top: 1px;
    text-decoration: none;
    color: #00788d;
  }
  .btn-right {
    color: #00788d;
    background-color: Transparent;
    background-repeat: no-repeat;
    border: none;
    cursor: pointer;
    overflow: hidden;
    outline: none;
    padding-right: 20px;
  }
  .icon-right {
    position: relative;
  }
  .icon-right:before {
    content: "\f061";
    font-family: FontAwesome;
    position: absolute;
    right: 0px;
  }
  .row-space-around{
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
  .m-vertical{
    margin: auto 0px;
  }
  ::placeholder { /* Chrome, Firefox, Opera, Safari 10.1+ */
    color: lightgray;
    opacity: 1; /* Firefox */
  }
  :-ms-input-placeholder { /* Internet Explorer 10-11 */
      color: lightgray;
  }
  ::-ms-input-placeholder { /* Microsoft Edge */
      color: lightgray;
  }

  /* Transactions container */
  .transactions-container {
    margin-bottom: 20px;
  }
  .transactions-cards-mobile {
    display: none;
  }
  .transactions-table-desktop {
    display: block;
  }

  /* Transaction cards for mobile */
  .transaction-card {
    border: 1px solid #eee;
    border-radius: 4px;
    margin-bottom: 16px;
    background-color: #fff;
    overflow: hidden;
  }
  .transaction-card-header {
    padding: 14px 16px;
    background-color: #fafbfc;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }
  .transaction-card-date {
    font-weight: 600;
    font-size: 16px;
    color: #333;
  }
  .transaction-card-body {
    padding: 14px 16px;
  }
  .transaction-card-row {
    display: flex;
    justify-content: space-between;
    padding: 10px 0;
    border-bottom: 1px solid #f5f5f5;
  }
  .transaction-card-row:last-child {
    border-bottom: none;
  }
  .transaction-card-amount {
    font-weight: 600;
  }
  .transaction-card-label {
    color: #888;
    font-size: 15px;
    font-weight: 500;
  }
  .transaction-card-value {
    color: #333;
    font-size: 15px;
    text-align: right;
  }
  .transaction-card-amount .transaction-card-value {
    font-weight: 600;
    font-size: 16px;
  }

  /* Mobile responsive styles */
  @media (max-width: 768px) {
    .content-wrapper {
      padding: 16px 12px;
    }

    .content-heading {
      font-size: 24px;
      margin-bottom: 20px;
    }

    .panel-default {
      margin-bottom: 20px;
      padding: 20px 16px;
    }

    .row-space-around {
      flex-direction: column;
      gap: 16px;
      align-items: stretch;
    }

    .current-balance {
      margin: 0;
      text-align: center;
    }

    .current-balance p {
      font-size: 18px;
      margin: 0;
    }

    .amountBalance {
      font-size: 22px !important;
      display: block;
      margin-top: 8px;
    }

    .btn-full-mobile {
      width: 100%;
      margin: 0;
    }

    .panel-title {
      font-size: 18px;
    }

    .mt-lg {
      margin-top: 16px;
    }

    .mb-lg {
      margin-bottom: 16px;
    }

    .mb-xl {
      margin-bottom: 20px;
    }

    /* Hide desktop table, show mobile cards */
    .transactions-table-desktop {
      display: none;
    }
    .transactions-cards-mobile {
      display: block;
    }

    /* Welcome section */
    .welcome {
      padding: 30px 16px;
    }

    .welcome-header {
      margin: 20px auto 0;
    }

    .welcome-title {
      font-size: 24px;
    }

    .welcome-paragraph {
      max-width: 100%;
      font-size: 16px;
      margin: 16px auto;
      padding: 0 8px;
    }

    .welcome-options {
      flex-direction: column;
      align-items: stretch;
      padding: 0 16px;
    }

    .welcome-options .card {
      max-width: 100%;
      width: 100%;
      margin: 10px 0;
    }

    .card {
      padding: 30px 20px;
    }

    .card-title {
      font-size: 20px;
    }

    .card p {
      font-size: 16px;
    }

    /* Updates section */
    .updates-header {
      flex-direction: column;
      gap: 12px;
      align-items: flex-start;
    }

    .latest-updates {
      font-size: 16px;
      width: 100%;
    }

    .latest-updates a {
      word-break: break-word;
    }

    .updates-content {
      flex-direction: column;
      gap: 16px;
    }

    .card-update-content {
      width: 100%;
      padding: 20px;
    }

    .card-icon {
      margin-bottom: 16px;
      height: 80px;
    }

    /* Alert styling */
    .alert {
      padding: 16px;
      font-size: 15px;
    }

    .alert h4 {
      font-size: 17px;
      margin-bottom: 10px;
    }

    .alert p {
      font-size: 15px;
    }
  }

  @media (max-width: 480px) {
    .content-wrapper {
      padding: 12px 8px;
    }

    .content-heading {
      font-size: 22px;
    }

    .panel-default {
      padding: 16px 12px;
    }

    .current-balance p {
      font-size: 17px;
    }

    .amountBalance {
      font-size: 20px !important;
    }

    .panel-title {
      font-size: 17px;
    }

    .welcome-title {
      font-size: 22px;
    }

    .welcome-paragraph {
      font-size: 15px;
    }

    .card-title {
      font-size: 18px;
    }

    .card p {
      font-size: 15px;
    }

    .transaction-card-header,
    .transaction-card-body {
      padding: 12px 14px;
    }

    .transaction-card-label,
    .transaction-card-value {
      font-size: 14px;
    }

    .transaction-card-amount .transaction-card-value {
      font-size: 15px;
    }

    .latest-updates {
      font-size: 15px;
    }

    .card-icon {
      height: 70px;
    }
  }

  @media (max-width: 360px) {
    .content-heading {
      font-size: 20px;
    }

    .panel-title {
      font-size: 16px;
    }

    .amountBalance {
      font-size: 19px !important;
    }

    .card-icon {
      height: 60px;
    }
  }
</style>



// WEBPACK FOOTER //
// AccountSummary.vue?589b19b4